export const shouldLoadMore = ({
  scrollTop,
  scrollHeight,
  clientHeight,
  threshold,
}: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  threshold?: number;
}) => {
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
  return distanceFromBottom <= 10 || scrollTop >= scrollHeight * (threshold || 0.6) - clientHeight;
};

type CollectionMetadata = { spotify_playlist?: string | null } & Record<string, unknown>;

/**
 * Parses metadata that might be a JSON string or already an object
 * @param metadata - Metadata as string or object
 * @returns Parsed metadata object or null
 */
export const parseMetadata = (
  metadata: string | CollectionMetadata | null | undefined
): CollectionMetadata | null => {
  if (!metadata) return null;
  if (typeof metadata === "string") {
    try {
      return JSON.parse(metadata) as CollectionMetadata;
    } catch {
      return null;
    }
  }
  return metadata;
};

/**
 * Extracts Spotify playlist ID from collection metadata
 * @param metadata - Collection metadata
 * @returns Spotify playlist ID or null
 */
export const extractSpotifyPlaylistId = (
  metadata: string | CollectionMetadata | null | undefined
): string | null => {
  const meta = parseMetadata(metadata);
  if (!meta?.spotify_playlist) return null;

  const url = meta.spotify_playlist;
  return url.includes("spotify.com") ? url.split("/playlist/")[1]?.split("?")[0] || url : url;
};

/**
 * Formats a date string in a specific timezone to YYYY-MM-DD format
 * @param dateString - Date string to format
 * @param timezone - IANA timezone identifier (e.g., "America/Los_Angeles")
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const formatDateInTimezone = (dateString: string | unknown, timezone: string): string => {
  const date = new Date(dateString as string);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`;
};

type ItemTimestampLike = {
  itemtimestamp?: string | null;
  lastchecked?: string | Date | null;
  lastseen?: string | Date | null;
  solddate?: string | Date | null;
  isSold?: boolean | null;
};

type ItemTimestampSource = "sold" | "seen" | "checked" | "added";

const toTimestampMs = (value: string | Date | null | undefined): number | null => {
  if (!value) return null;

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const formatRelativeTimestamp = (timestampMs: number, nowMs: number): string => {
  const seconds = Math.max(0, Math.floor((nowMs - timestampMs) / 1000));

  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;

  const date = new Date(timestampMs);
  const sameYear = date.getFullYear() === new Date(nowMs).getFullYear();
  const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return sameYear ? dateStr : `${dateStr} '${String(date.getFullYear()).slice(-2)}`;
};

const timestampSourceLabels: Record<ItemTimestampSource, string> = {
  sold: "Marked sold",
  seen: "Updated",
  checked: "Updated",
  added: "Added",
};

export const getItemTimestampDisplay = (options: {
  item: ItemTimestampLike;
  showAddedWhenUpdated?: boolean;
  showLabel?: boolean;
  forceAdded?: boolean;
  now?: Date;
}): string | null => {
  const {
    item,
    showAddedWhenUpdated = false,
    showLabel = false,
    now = new Date(),
    forceAdded = false,
  } = options;
  const nowMs = now.getTime();

  if (item?.isSold) {
    const soldAtMs = toTimestampMs(item.solddate);
    if (soldAtMs !== null) {
      return `${showLabel ? `${timestampSourceLabels.sold} ` : ""}${formatRelativeTimestamp(soldAtMs, nowMs)}`;
    }
    const lastSeenMs = toTimestampMs(item.lastseen);
    const lastCheckedMs = toTimestampMs(item.lastchecked);
    const fallbackMs =
      lastSeenMs !== null && (lastCheckedMs === null || lastSeenMs >= lastCheckedMs)
        ? lastSeenMs
        : lastCheckedMs;
    if (fallbackMs !== null) {
      return `${timestampSourceLabels.sold} ${formatRelativeTimestamp(fallbackMs, nowMs)}`;
    }
    return null;
  }

  const lastSeenMs = toTimestampMs(item.lastseen);
  const lastCheckedMs = toTimestampMs(item.lastchecked);
  const itemTimestampMs = toTimestampMs(item.itemtimestamp);

  let source: ItemTimestampSource | null = null;
  let timestampMs: number | null = null;

  if (forceAdded && itemTimestampMs) {
    return `${showLabel ? `${timestampSourceLabels["added"]} ` : ""}${formatRelativeTimestamp(itemTimestampMs, nowMs)}`;
  }

  if (lastSeenMs !== null || lastCheckedMs !== null) {
    if (lastSeenMs !== null && (lastCheckedMs === null || lastSeenMs >= lastCheckedMs)) {
      source = "seen";
      timestampMs = lastSeenMs;
    } else if (lastCheckedMs !== null) {
      source = "checked";
      timestampMs = lastCheckedMs;
    }
  } else {
    if (itemTimestampMs !== null) {
      source = "added";
      timestampMs = itemTimestampMs;
    }
  }

  if (!source || timestampMs === null) return null;

  const formattedTime = formatRelativeTimestamp(timestampMs, nowMs);
  let display = `${showLabel ? `${timestampSourceLabels[source]} ` : ""}${formattedTime}`;

  if (showAddedWhenUpdated && source !== "added" && itemTimestampMs) {
    const addedText = formatRelativeTimestamp(itemTimestampMs, nowMs);
    if (addedText !== formattedTime) {
      display = display + ` (Added ${addedText})`;
    } else {
      display = `Added ${addedText}`;
    }
  }

  return display;
};
